import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

/**
 * Collects the required per-owner action before saving a device change that would remove access to existing fluxes.
 */
const DeviceOwnershipResolutionDialog = ({
    open = false,
    impactedOwners = [],
    futureOwner = null,
    deleteAllowed = false,
    resolutions = {},
    onResolutionChange,
    onClose,
    onConfirm,
    error = '',
}) => {
    const [expandedOwnerIds, setExpandedOwnerIds] = useState({});

    const dialogEntries = useMemo(() => {
        return Array.isArray(impactedOwners) ? impactedOwners : [];
    }, [impactedOwners]);

    const formatDeviceLabel = (device) => {
        const capacityLabel = `${device.current_fluxes}/${device.max_fluxes} fluxes`;
        const availabilityLabel = device.is_full ? 'full' : `${device.available_slots} free`;
        const scopeLabel = device.system ? 'system-wide' : 'owner device';

        return `${device.name} - ${capacityLabel} - ${availabilityLabel} - ${scopeLabel}`;
    };

    const toggleOwnerDetails = (ownerId) => {
        setExpandedOwnerIds((current) => ({
            ...current,
            [ownerId]: !current[ownerId],
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Resolve flux ownership before save</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    These changes would leave some flux owners without access to this device. Choose what to do for each affected account before saving.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {dialogEntries.map((entry) => {
                    const user = entry.user || {};
                    const resolution = resolutions[user.id] || { action: '', target_device_id: '' };
                    const candidateDevices = Array.isArray(entry.candidate_devices) ? entry.candidate_devices : [];
                    const isExpanded = Boolean(expandedOwnerIds[user.id]);

                    return (
                        <Box key={user.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2 }}>
                            <Typography variant="subtitle2">
                                {user.name} {user.email ? `(${user.email})` : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {entry.item_count} flux{entry.item_count === 1 ? '' : 'es'} will lose access to this device.
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                <Button size="small" variant="outlined" onClick={() => toggleOwnerDetails(user.id)}>
                                    {isExpanded ? 'Hide affected fluxes' : 'View affected fluxes'}
                                </Button>
                                <Chip size="small" label={`${entry.item_count} impacted`} />
                            </Stack>

                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, backgroundColor: 'background.default' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Affected fluxes
                                    </Typography>
                                    <List dense disablePadding>
                                        {(entry.items || []).map((item) => (
                                            <ListItem key={item.id} alignItems="flex-start" disableGutters sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <ListItemText
                                                    primary={`${item.id} - ${item.name || 'Unnamed flux'}`}
                                                    secondary={
                                                        <>
                                                            <Typography component="span" variant="body2" color="text.primary">
                                                                Group: {item.group || '-'}
                                                            </Typography>
                                                            <br />
                                                            <Typography component="span" variant="body2" color="text.primary">
                                                                Client: {item.client_name || '-'} {item.client_email ? `(${item.client_email})` : ''}
                                                            </Typography>
                                                            <br />
                                                            Status: {item.status || '-'}
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </Collapse>

                            <FormControl component="fieldset" fullWidth>
                                <FormLabel component="legend">Choose resolution</FormLabel>
                                <RadioGroup
                                    value={resolution.action || ''}
                                    onChange={(event) => onResolutionChange(user.id, {
                                        ...resolution,
                                        action: event.target.value,
                                    })}
                                >
                                    <FormControlLabel
                                        value="move_to_device"
                                        control={<Radio />}
                                        disabled={candidateDevices.length === 0}
                                        label="Option 1: move these fluxes to another device available to this account"
                                    />
                                    {resolution.action === 'move_to_device' && (
                                        <Box sx={{ ml: 4, mt: 1, mb: 1.5 }}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Target device"
                                                value={resolution.target_device_id || ''}
                                                onChange={(event) => onResolutionChange(user.id, {
                                                    ...resolution,
                                                    target_device_id: event.target.value,
                                                })}
                                            >
                                                {candidateDevices.map((device) => (
                                                    <MenuItem key={device.id} value={String(device.id)} disabled={device.is_full}>
                                                        {formatDeviceLabel(device)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                Capacity is based on current flux count per device. System-wide devices are listed when available.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                After moving, copy false positives to the destination device if you still need them there.
                                            </Typography>
                                        </Box>
                                    )}

                                    <FormControlLabel
                                        value="transfer_to_owner"
                                        control={<Radio />}
                                        label={`Option 2: transfer these fluxes to ${futureOwner?.name || 'the future owner'} account where the device will remain`}
                                    />

                                    {deleteAllowed && (
                                        <FormControlLabel
                                            value="delete_items"
                                            control={<Radio />}
                                            label="Option 3: permanently delete these fluxes"
                                        />
                                    )}
                                </RadioGroup>
                            </FormControl>

                            {resolution.action === 'delete_items' && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>
                                    This is permanent and will permanently delete the affected fluxes.
                                </Alert>
                            )}
                        </Box>
                    );
                })}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onConfirm}>Continue save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeviceOwnershipResolutionDialog;